"use client";

import type { GetDateByRangeProps } from "@packages/sdk";
import { getDateByRange, useRequestCommunitiesAdminGraph } from "@packages/sdk";
import { useMemo } from "react";

import { useDynamicViewport } from "../../../../../hooks/useDynamicViewport";
import { useErrorNotification } from "../../../../../hooks/useErrorNotification";
import { LineChart } from "../../../../_base/dashboard/charts/LineChart/LineChart";

export type LineChartPrayersCompletedProps = {
  id: number;
  range?: GetDateByRangeProps["range"] | "all";
};

export const LineChartPrayersCompleted = ({
  id,
  range = "all",
}: LineChartPrayersCompletedProps) => {
  const isMobileViewport = useDynamicViewport({ minimumWidth: 540 });
  const isTabletViewport = useDynamicViewport({ minimumWidth: 768 });

  const { query: queryCommunitiesAdminGraphWeekly } =
    useRequestCommunitiesAdminGraph({
      id,
      startDate: getDateByRange({ range: "weekly" }),
    });

  const { query: queryCommunitiesAdminGraphMonthly } =
    useRequestCommunitiesAdminGraph({
      id,
      startDate: getDateByRange({ range: "monthly" }),
    });

  const { query: queryCommunitiesAdminGraphQuarterly } =
    useRequestCommunitiesAdminGraph({
      id,
      startDate: getDateByRange({ range: "quarterly" }),
    });

  const { query: queryCommunitiesAdminGraphYearly } =
    useRequestCommunitiesAdminGraph({
      id,
      startDate: getDateByRange({ range: "yearly" }),
    });

  const { query: queryCommunitiesAdminGraphAll } =
    useRequestCommunitiesAdminGraph({ id });

  const getQueryCommunitiesAdminGraph = () => {
    switch (range) {
      case "weekly":
        return queryCommunitiesAdminGraphWeekly;
      case "monthly":
        return queryCommunitiesAdminGraphMonthly;
      case "quarterly":
        return queryCommunitiesAdminGraphQuarterly;
      case "yearly":
        return queryCommunitiesAdminGraphYearly;
      default:
        return queryCommunitiesAdminGraphAll;
    }
  };

  const queryCommunitiesAdminGraph = getQueryCommunitiesAdminGraph();

  useErrorNotification({
    isError: queryCommunitiesAdminGraph.isError,
  });

  const xAxisInterval = useMemo(() => {
    if (isTabletViewport) return 0;

    if (isMobileViewport) {
      if (range === "all" || range === "quarterly") return 3;
    }

    if (range === "all" || range === "quarterly") return 4;
    if (range === "yearly") return 1;

    return 0;
  }, [isMobileViewport, isTabletViewport, range]);

  return (
    <LineChart
      data={queryCommunitiesAdminGraph.data?.data}
      loading={!queryCommunitiesAdminGraph.data}
      xAxisInterval={xAxisInterval}
    />
  );
};

LineChartPrayersCompleted.displayName = "LineChartPrayersCompleted";
